import { catchError, from, map, Observable, of, switchMap } from 'rxjs';

import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { typedConverter } from '@verify/shared-components/helpers';
import { Auth } from 'firebase/auth';
import {
  addDoc,
  collection,
  collectionGroup,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentChange,
  DocumentData,
  DocumentReference,
  endAt,
  Firestore,
  getCountFromServer,
  getDoc,
  onSnapshot,
  orderBy,
  Query,
  query,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  QueryNonFilterConstraint,
  startAt,
  UpdateData,
  updateDoc,
  WithFieldValue,
  writeBatch,
} from 'firebase/firestore';
import { execute, Pipeline } from 'firebase/firestore/pipelines';
import { ChangeNotifierService } from './change-notifier.service';
import { FIREBASE_AUTH, FIREBASE_FIRESTORE } from './firebase.tokens';

export enum BatchActionType {
  add = 'add',
  update = 'update',
  delete = 'delete',
}

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  private firestore: Firestore = inject(FIREBASE_FIRESTORE);
  private auth: Auth = inject(FIREBASE_AUTH);
  private changeNotifier = inject(ChangeNotifierService);

  private static tenantCollection = 'tenants';

  constructor() {}

  getCollection<T extends DocumentData>(
    collectionPath: string,
  ): Observable<T[]> {
    const collectionRef = this.getCollectionRef<T>(collectionPath);
    return this.collectionData(collectionRef);
  }

  getCollectionChanges<T extends DocumentData>(
    collectionPath: string,
  ): Observable<DocumentChange<T>[]> {
    const collectionRef = this.getCollectionRef<T>(collectionPath);
    return this.collectionChanges(collectionRef);
  }

  getCollectionGroup<T extends DocumentData>(
    collectionId: string,
  ): Observable<T[]> {
    const collectionRef = this.getCollectionGroupQuery<T>(collectionId);
    return this.collectionData(collectionRef);
  }

  getDocument<T extends DocumentData>(
    documentPath: string,
  ): Observable<T | null> {
    const docRef = this.getDocRef<T>(documentPath);
    return from(getDoc(docRef)).pipe(
      switchMap((snapshot) =>
        snapshot.exists() ? this.docData(docRef) : of(null),
      ),
      map((doc) => doc ?? null),
      catchError((error) => {
        Sentry.captureException(error);
        return of(null);
      }),
    );
  }

  getQuery<T extends DocumentData>(
    collectionPath: string,
    ...queryConstraints: QueryConstraint[]
  ): Observable<T[]>;
  getQuery<T extends DocumentData>(
    collectionPath: string,
    compositeFilter: QueryCompositeFilterConstraint,
    ...queryConstraints: QueryNonFilterConstraint[]
  ): Observable<T[]>;
  getQuery<T extends DocumentData>(
    collectionPath: string,
    ...p2: unknown[]
  ): Observable<T[]> {
    const collectionRef = this.getCollectionRef<T>(collectionPath);
    const queryRef = query(collectionRef, ...(p2 as QueryConstraint[]));
    return this.collectionData(queryRef);
  }

  getPipelineCollection(collectionPath: string): Pipeline {
    return this.firestore
      .pipeline()
      .collection(this.getCollectionRef(collectionPath));
  }

  executePipeline<T extends DocumentData>(pipeline: Pipeline): Observable<T[]> {
    return from(execute(pipeline)).pipe(
      map((snapshot) =>
        snapshot.results.map(
          (result) => ({ ...result.data(), id: result.id }) as unknown as T,
        ),
      ),
    );
  }

  getCount<T extends DocumentData>(
    collectionPath: string,
    ...queryConstraints: QueryConstraint[]
  ): Observable<number> {
    const collectionRef = this.getCollectionRef<T>(collectionPath);
    const queryRef = query(collectionRef, ...queryConstraints);
    return from(getCountFromServer(queryRef)).pipe(
      map((value) => value.data().count),
      this.changeNotifier.notifyOnEmit(),
    );
  }

  getGroupQuery<T extends DocumentData>(
    collectionId: string,
    queryConstraints: QueryConstraint[],
  ): Observable<T[]> {
    const collectionRef = this.getCollectionGroupQuery<T>(collectionId);
    const queryRef = query(collectionRef, ...queryConstraints);
    return this.collectionData(queryRef);
  }

  addDocument<T extends DocumentData>(
    collectionPath: string,
    document: T | Partial<T>,
  ): Observable<T | undefined> {
    const collectionRef = this.getCollectionRef<T>(collectionPath);
    return from(addDoc(collectionRef, document as WithFieldValue<T>)).pipe(
      switchMap((docRef) => this.docData(docRef)),
      this.changeNotifier.notifyOnEmit(),
    );
  }

  deleteDocument<T extends DocumentData>(
    documentPath: string,
  ): Observable<void> {
    const docRef =
      this.getDocRef(documentPath).withConverter(typedConverter<T>());
    return from(deleteDoc(docRef)).pipe(this.changeNotifier.notifyOnEmit());
  }

  updateDocument<T extends DocumentData>(
    documentPath: string,
    data: UpdateData<T>,
  ): Observable<void> {
    const docRef =
      this.getDocRef(documentPath).withConverter(typedConverter<T>());
    return from(updateDoc(docRef, data)).pipe(
      this.changeNotifier.notifyOnEmit(),
    );
  }

  batchWrite(
    actions: Array<{
      documentPath: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data?: UpdateData<any>;
      type: BatchActionType;
    }>,
  ): Observable<void> {
    const batch = writeBatch(this.firestore);
    actions.forEach(({ documentPath, data, type }) => {
      const docRef = this.getDocRef(documentPath);
      switch (type) {
        case 'add': {
          batch.set(docRef, data);
          break;
        }
        case 'update': {
          batch.update(docRef, data);
          break;
        }
        case 'delete': {
          batch.delete(docRef);
          break;
        }
      }
    });
    return from(batch.commit()).pipe(this.changeNotifier.notifyOnEmit());
  }

  getCollectionRef<T extends DocumentData>(
    collectionName: string,
  ): CollectionReference<T, T> {
    return collection(
      this.firestore,
      this.getTenantPath(),
      collectionName,
    ).withConverter(typedConverter<T>()) as CollectionReference<T, T>;
  }

  getDocRef<T extends DocumentData>(
    documentPath: string,
  ): DocumentReference<T, T> {
    return doc(
      this.firestore,
      this.getTenantPath(),
      documentPath,
    ).withConverter(typedConverter<T>()) as DocumentReference<T, T>;
  }

  private collectionData<T extends DocumentData>(
    ref: CollectionReference<T> | Query<T>,
  ): Observable<T[]> {
    return new Observable<T[]>((subscriber) => {
      return onSnapshot(
        ref,
        (snapshot) =>
          subscriber.next(
            snapshot.docs.map((d) => ({ ...d.data(), id: d.id }) as T),
          ),
        (error) => subscriber.error(error),
      );
    }).pipe(this.changeNotifier.notifyOnEmit());
  }

  private docData<T extends DocumentData>(
    ref: DocumentReference<T>,
  ): Observable<T | undefined> {
    return new Observable<T | undefined>((subscriber) => {
      return onSnapshot(
        ref,
        (snapshot) =>
          subscriber.next(
            snapshot.exists()
              ? ({ ...snapshot.data(), id: snapshot.id } as T)
              : undefined,
          ),
        (error) => subscriber.error(error),
      );
    }).pipe(this.changeNotifier.notifyOnEmit());
  }

  private collectionChanges<T extends DocumentData>(
    ref: CollectionReference<T> | Query<T>,
  ): Observable<DocumentChange<T>[]> {
    return new Observable<DocumentChange<T>[]>((subscriber) => {
      return onSnapshot(
        ref,
        (snapshot) => subscriber.next(snapshot.docChanges()),
        (error) => subscriber.error(error),
      );
    }).pipe(this.changeNotifier.notifyOnEmit());
  }

  private getCollectionGroupQuery<T extends DocumentData>(
    collectionId: string,
  ): Query<T, T> {
    const group = collectionGroup(this.firestore, collectionId).withConverter(
      typedConverter<T>(),
    );
    const tenantDoc = this.getDocRef<DocumentData>('');
    return query(
      group,
      orderBy('__name__'),
      startAt(tenantDoc.path),
      endAt(`${tenantDoc.path}`),
    ) as Query<T, T>;
  }

  private getTenantPath(): string {
    return `${FirestoreService.tenantCollection}/${this.auth.tenantId}`;
  }
}
